import { Loader2, Info } from "lucide-react";
import { Switch } from "./ui/switch";
import { ListenButton } from "./ListenButton";

const CLASS_LABEL = { beginning: "Beginning degree · 0°", anaretic: "Anaretic degree · 29°", ordinary: "Ordinary degree" };
const COMP_LABEL = { planet: "Planet", sign: "Sign", degree: "Degree", house: "House", retrograde: "Retrograde", decan: "Decan · extra layer", aspects: "Aspects" };

function Block({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary/80 mb-1">{title}</p>
      <div className="text-sm text-foreground/90 leading-relaxed">{children}</div>
    </div>
  );
}

function ListBlock({ title, items, testid }) {
  if (!items?.length) return null;
  return (
    <Block title={title} testid={testid}>
      <ul className="list-disc pl-4 space-y-1">{items.map((x) => <li key={x}>{x}</li>)}</ul>
    </Block>
  );
}

function Facts({ r }) {
  const facts = [CLASS_LABEL[r.degree_classification],
    r.house ? `House ${r.house}` : null, r.retrograde === true ? "Retrograde" : r.retrograde === false ? "Direct" : null];
  return (
    <div className="flex flex-wrap gap-2" data-testid="degree-reading-facts">
      {facts.filter(Boolean).map((f) => (
        <span key={f} className="font-mono text-[11px] px-2.5 py-1 rounded-full border border-primary/30 text-primary">{f}</span>
      ))}
    </div>
  );
}

export function DegreeReading({ reading: r, includeQuestions, onToggleQuestions, refreshing }) {
  const comps = Object.entries(r.components || {});
  return (
    <div className="space-y-5 mt-3" data-testid="degree-reading">
      <Facts r={r} />
      <div>
        <h3 className="font-serif text-2xl text-foreground" data-testid="degree-reading-headline">{r.headline}</h3>
        <p className="text-sm text-foreground/90 leading-relaxed mt-2" data-testid="degree-reading-summary">{r.summary}</p>
        <ListenButton testid="listen-degree-reading" label="Listen to this reading" className="mt-2"
          text={[r.headline, r.summary, r.central_theme, r.constructive_expression, r.overextended_expression, r.integration_theme].join(" ")} />
      </div>
      <Block title="Central theme" testid="degree-reading-theme">{r.central_theme}</Block>
      <div className="grid sm:grid-cols-2 gap-4" data-testid="degree-reading-components">
        {comps.map(([k, v]) => <Block key={k} title={COMP_LABEL[k] || k} testid={`degree-component-${k}`}>{v}</Block>)}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Block title="Constructive expression" testid="degree-reading-constructive">{r.constructive_expression}</Block>
        <Block title="Possible overextension" testid="degree-reading-overextended">{r.overextended_expression}</Block>
        <Block title="Integration theme" testid="degree-reading-integration">{r.integration_theme}</Block>
      </div>
      <ListBlock title="Everyday examples (hypothetical)" items={r.everyday_examples} testid="degree-reading-examples" />
      <div className="flex items-center gap-3">
        <Switch checked={includeQuestions} onCheckedChange={onToggleQuestions} disabled={refreshing} data-testid="degree-reading-questions-toggle" />
        <span className="text-xs text-muted-foreground">Reflection questions</span>
        {refreshing && <Loader2 size={12} className="animate-spin text-primary" />}
      </div>
      <ListBlock title="Reflection questions" items={r.reflection_questions} testid="degree-reading-questions" />
      {r.data_limitations?.length > 0 && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="degree-reading-limitations">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Data limitations</p>
          <ul className="text-xs text-muted-foreground space-y-0.5">{r.data_limitations.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}
      <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground italic" data-testid="degree-reading-note">
        <Info size={12} className="mt-0.5 shrink-0" /> {r.interpretive_note}
      </p>
    </div>
  );
}

const REASON_TEXT = {
  missing_body: "this placement isn't in your stored chart",
  missing_sign: "the sign for this placement is missing",
  missing_position: "the exact position for this placement is missing",
  unverifiable_position: "the exact position can't be verified (for Rising, a known birth time is required)",
  unsupported_placement: "this point isn't supported for degree-aware readings",
};

export function DegreeReadingError({ reason, onClassic }) {
  return (
    <div className="mt-2 space-y-3" data-testid="degree-reading-error">
      <p className="text-sm text-foreground/90">A degree-aware reading isn't available: {REASON_TEXT[reason] || "the chart data couldn't be verified"}.</p>
      <button onClick={onClassic} data-testid="degree-reading-classic-btn"
        className="text-xs uppercase tracking-widest text-primary hover:text-foreground transition-colors">
        Show classic reading instead
      </button>
    </div>
  );
}
